import { useCallback, useEffect, useRef, useState } from 'react';

function getLocalStorageValue<T>(key: string, initialValue: T): T {
    const item = localStorage.getItem(key);

    if (item === null) {
        return initialValue;
    }

    try {
        return JSON.parse(item);
    } catch (error) {
        console.error(`Error parsing localStorage key "${key}":`, error);
        return initialValue;
    }
}

function setLocalStorageValue<T>(key: string, value: T): void {
    localStorage.setItem(key, JSON.stringify(value));
}

export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: ((prev: T) => T)|T) => void] {
    const [ storedValue, setStoredValue ] = useState<T>(() => getLocalStorageValue(key, initialValue));
    const storedValueRef = useRef<T>(storedValue);

    useEffect(() => {
        const updateValue = () => {
            const newValue = getLocalStorageValue(key, initialValue);

            if (newValue === storedValueRef.current) {
                return;
            }

            storedValueRef.current = newValue;
            setStoredValue(newValue);
        };
        
        window.addEventListener('storage', updateValue);
        updateValue();

        return () => {
            window.removeEventListener('storage', updateValue);
        };
    }, [ key ]);

    const setValue = useCallback((value: ((prev: T) => T)|T) => {
        const newValue = typeof value === 'function'
            ? (value as (prev: T) => T)(storedValueRef.current)
            : value;

        if (newValue !== storedValueRef.current) {
            storedValueRef.current = newValue;
            setLocalStorageValue(key, newValue);
            setStoredValue(newValue);
        }
    }, [ key, storedValueRef ]);

    return [ storedValue, setValue ];
}
